import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, Check, Eye } from 'lucide-react'
import { Chip, Button, useToast } from '../ds/index'
import { Icon } from './Icon'
import { useSession, type Me, type Role } from '../lib/session'
import { api, post, del } from '../lib/api'

type Person = { id: string; name: string; email: string; role: 'admin' | 'rep' | 'buyer'; groups: number; subscribers: number; open_leads: number }

const initials = (name: string) => name.split(' ').map((x) => x[0]).join('').slice(0, 2).toUpperCase()

export function roleLabel(role: Role, org: string) {
  if (role === 'spec') return 'SPEC'
  if (role === 'admin') return `${org} admin`
  if (role === 'buyer') return 'Client'
  return 'Rep'
}

/** What each login is for, in one line. Shown in the switcher and on People. */
export function roleSees(role: Role | 'subscriber', org: string) {
  if (role === 'spec') return `Everything, plus Economics and AI cost. ${org} never sees those two.`
  if (role === 'admin') return 'Runs the operation: dashboard, lead board, emails in, groups, subscribers, people and settings.'
  if (role === 'rep') return 'Only their own leads, built for a phone. Open the post, reply on Facebook, upload the screenshot.'
  return 'No login. Each time a rep recommends them, they get an email. Written today, not sent yet.'
}

/**
 * Top-right identity. SPEC staff get a switcher to look at the app as any
 * admin or rep; everyone else sees who they are signed in as.
 */
export default function ViewAs({ me }: { me: Me }) {
  const org = me.org.name
  const viewing = me.viewing_as
  if (!me.can_view_as) {
    return (
      <span className="view-as-static">
        <span className="avatar avatar-sm" aria-hidden="true">{initials(me.user.name)}</span>
        <span className="hide-phone view-as-name">{me.user.name}</span>
        <Chip tone={me.user.role === 'spec' ? 'accent' : undefined}>{roleLabel(me.user.role, org)}</Chip>
      </span>
    )
  }
  return <Switcher me={me} org={org} viewing={Boolean(viewing)} />
}

function Switcher({ me, org, viewing }: { me: Me; org: string; viewing: boolean }) {
  const [open, setOpen] = useState(false)
  const [people, setPeople] = useState<Person[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const wrap = useRef<HTMLDivElement>(null)
  const { refresh } = useSession()
  const navigate = useNavigate()
  const toast = useToast()

  useEffect(() => {
    if (!open) return
    api<{ people: Person[] }>('/api/view-as').then((r) => setPeople(r.people)).catch((e) => toast((e as Error).message, 'neg'))
    const onDown = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open, toast])

  const go = async (id: string | null) => {
    setBusy(id || 'self')
    try {
      if (id) await post('/api/view-as', { user_id: id })
      else await del('/api/view-as')
      await refresh()
      setOpen(false)
      navigate('/')
    } catch (e) {
      toast((e as Error).message, 'neg')
    } finally {
      setBusy(null)
    }
  }

  const admins = (people || []).filter((p) => p.role === 'admin')
  const reps = (people || []).filter((p) => p.role === 'rep')
  const clients = (people || []).filter((p) => p.role === 'buyer')
  const current = viewing ? me.user.id : null
  const realName = me.viewing_as?.real_name || me.user.name

  const row = (id: string | null, name: string, meta: string) => (
    <button key={id || 'self'} className="view-as-item" onClick={() => go(id)} disabled={busy !== null}
            aria-current={current === id || undefined}>
      <span className="avatar avatar-sm" aria-hidden="true">{initials(name)}</span>
      <span className="view-as-item-text"><strong>{name}</strong><span className="muted">{meta}</span></span>
      {current === id ? <Icon of={Check} /> : busy === (id || 'self') ? <span className="muted">...</span> : null}
    </button>
  )

  return (
    <div className="view-as" ref={wrap}>
      <button className={viewing ? 'view-as-trigger is-viewing' : 'view-as-trigger'} onClick={() => setOpen((o) => !o)}
              aria-haspopup="dialog" aria-expanded={open}>
        {viewing && <Icon of={Eye} />}
        <span className="avatar avatar-sm" aria-hidden="true">{initials(me.user.name)}</span>
        <span className="view-as-name hide-phone">{me.user.name}</span>
        <Chip tone={me.user.role === 'spec' ? 'accent' : viewing ? 'warn' : undefined}>{roleLabel(me.user.role, org)}</Chip>
        <Icon of={ChevronDown} />
      </button>
      {open && (
        <div className="view-as-panel" role="dialog" aria-label="See the app as">
          <div className="view-as-head">
            <strong>See the app as</strong>
            <span className="muted">Who gets which login, and exactly what they see. Changes are off while you look.</span>
          </div>

          <Section title="SPEC" note={roleSees('spec', org)}>
            {row(null, realName, 'Your own view')}
          </Section>
          <Section title={`${org} admin`} note={roleSees('admin', org)}>
            {!people ? <span className="muted view-as-empty">Loading</span>
              : admins.length ? admins.map((p) => row(p.id, p.name, p.email))
              : <span className="muted view-as-empty">No admin logins yet</span>}
          </Section>
          <Section title={`Reps${people ? ` (${reps.length})` : ''}`} note={roleSees('rep', org)}>
            {!people ? <span className="muted view-as-empty">Loading</span>
              : reps.length ? reps.map((p) => row(p.id, p.name, `${p.groups} groups · ${p.subscribers} subscribers · ${p.open_leads} waiting`))
              : <span className="muted view-as-empty">No rep logins yet</span>}
          </Section>
          <Section title={`Client logins${people ? ` (${clients.length})` : ''}`} note="Their own portal: leads sent to them, the rep's screenshot, their balance and statements.">
            {!people ? <span className="muted view-as-empty">Loading</span>
              : clients.length ? clients.map((p) => row(p.id, p.name, p.email))
              : <span className="muted view-as-empty">No client logins yet. Add them on a client's balance page.</span>}
          </Section>
        </div>
      )}
    </div>
  )
}

function Section({ title, note, children }: { title: string; note: string; children?: React.ReactNode }) {
  return (
    <div className="view-as-section">
      <div className="view-as-section-head"><span className="view-as-section-title">{title}</span><span className="muted">{note}</span></div>
      {children && <div className="view-as-list">{children}</div>}
    </div>
  )
}

/** The strip across the top of every page while SPEC is looking as someone. */
export function ViewingBar({ me }: { me: Me }) {
  const { refresh } = useSession()
  const navigate = useNavigate()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  if (!me.viewing_as) return null
  const back = async () => {
    setBusy(true)
    try {
      const r = await del<{ client_id: string | null }>('/api/view-as')
      // Leaving a portal preview goes back to that client's page, with the admin app loaded again.
      if (r?.client_id) { window.location.assign(`/clients/${r.client_id}`); return }
      await refresh(); navigate('/')
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <div className="viewing-bar" role="status">
      <Icon of={Eye} />
      {me.viewing_as.client_preview ? (
        <span>
          Previewing <strong>{me.user.name}</strong>'s portal. This is exactly what they see.
          <span className="muted"> Read-only: nothing can be changed or paid from here.</span>
        </span>
      ) : (
        <span>
          Viewing as <strong>{me.user.name}</strong> ({roleLabel(me.user.role, me.org.name).toLowerCase()}). This is exactly what they see.
          <span className="muted"> Changes are off, so nothing is recorded under their name.</span>
        </span>
      )}
      <Button variant="secondary" onClick={back} disabled={busy}>{me.viewing_as.client_preview ? `Back to ${me.user.name}` : 'Back to my view'}</Button>
    </div>
  )
}
